import { useState, useEffect, useRef, useId } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '@/store/useTheme'
import { PALETTES } from '@/store/palettes'
import { useSession } from '@/store/useSession'
import { loadBackdropBlob, saveBackdropBlob, resizeImage } from '@/utils/clientBackdrop'
import styles from './ClientHomePage.module.css'

const OPTIONS = [
  {
    id: 'next-session',
    label: 'Your next session',
    subtitle: 'Join when your practitioner is ready',
    path: '/session/start',
  },
  {
    id: 'your-space',
    label: 'Your Space',
    subtitle: 'Your notes, photos and colours',
    path: '/session/after',
  },
] as const

function CameraIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8
               a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  )
}

function TickIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 12 12" fill="none"
      stroke="currentColor" strokeWidth="2.2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="2 6 5 9 10 3" />
    </svg>
  )
}

export default function ClientHomePage() {
  const navigate = useNavigate()
  const { palette, setPalette } = useTheme()
  const { clientName } = useSession()
  const uploadId = useId()
  const fileRef = useRef<HTMLInputElement>(null)
  const objUrlRef = useRef<string | null>(null)

  const [imageUrl, setImageUrl] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    loadBackdropBlob()
      .then(blob => {
        if (!blob || !active) return
        const url = URL.createObjectURL(blob)
        objUrlRef.current = url
        setImageUrl(url)
      })
      .catch(() => { /* IDB unavailable */ })
    return () => {
      active = false
      if (objUrlRef.current) {
        URL.revokeObjectURL(objUrlRef.current)
        objUrlRef.current = null
      }
    }
  }, [])

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''

    try {
      const resized = await resizeImage(file)
      if (objUrlRef.current) URL.revokeObjectURL(objUrlRef.current)
      const url = URL.createObjectURL(resized)
      objUrlRef.current = url
      setImageUrl(url)
      await saveBackdropBlob(resized)
    } catch (err) {
      console.warn('[ClientHome] image resize/save failed:', err)
    }
  }

  const paletteVars = {
    '--ch-bg':   palette.bg,
    '--ch-text': palette.text,
    '--ch-ring': palette.ring,
  } as React.CSSProperties

  const greeting = clientName ? `Hello, ${clientName}` : 'Hello'

  return (
    <main className={styles.page} style={paletteVars} aria-label="Client home">
      {imageUrl && (
        <div
          className={styles.backdrop}
          style={{ backgroundImage: `url(${imageUrl})` }}
          aria-hidden="true"
        />
      )}

      {imageUrl && (
        <div
          className={styles.backdropScrim}
          style={{ background: `color-mix(in srgb, ${palette.bg} 78%, transparent)` }}
          aria-hidden="true"
        />
      )}

      <div className={styles.content}>
        <div className={styles.personalise} aria-label="Personalise your space">
        <label
          htmlFor={uploadId}
          className={styles.cameraBtn}
          aria-label="Upload a photo for your space"
          title="Upload a photo"
        >
          <CameraIcon />
          <input
            id={uploadId}
            ref={fileRef}
            type="file"
            accept="image/*"
            className={styles.hiddenInput}
            onChange={handleFile}
            aria-hidden="true"
            tabIndex={-1}
          />
        </label>

        <div className={styles.paletteRow} role="group" aria-label="Choose colour palette">
          {PALETTES.map(p => (
            <button
              key={p.id}
              type="button"
              className={styles.paletteCircle}
              style={{ background: p.swatch }}
              data-active={palette.id === p.id}
              onClick={() => setPalette(p.id)}
              aria-label={p.label}
              aria-pressed={palette.id === p.id}
              title={p.label}
            >
              {palette.id === p.id && <TickIcon />}
            </button>
          ))}
        </div>
        </div>

        <div className={styles.inner}>
          <header className={styles.header}>
            <h1 className={styles.heading}>{greeting}</h1>
            <p className={styles.subtitle}>What would you like to do?</p>
          </header>

          <div className={styles.options} role="group" aria-label="Client options">
            {OPTIONS.map(opt => (
              <button
                key={opt.id}
                type="button"
                className={styles.optionCard}
                onClick={() => navigate(opt.path)}
              >
                <span className={styles.optionLabel}>{opt.label}</span>
                <span className={styles.optionSub}>{opt.subtitle}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </main>
  )
}
